import { useEffect, useRef, useCallback } from 'react'

import { useAppDispatch, useAppSelector } from 'app/state'
import {
  setMapFilterOrder,
  setMapFilterPagination,
  setMapFilterSearch,
  setMyMapsStatus,
  setMapFilter
} from 'features/maps/state/mapsSlice'
import { RequestStatus, SortDirection } from 'features/common/types'
import { MapFieldToOrderBy, MapOwnerFilter } from 'features/maps/types'
import BasicPageFilterBar from 'features/common/ui/BasicPage/Partials/BasicPageFilterBar'

import MapsOrderSelector from './MapsOrderSelector'
import MapsFilterSelector from './MapsFilterSelector'
import MapsSearchBar, { MapsSearchBarRefProps } from './MapsSearchBar'
import MapsClearSearch from './MapsClearSearch'
import { isEqual } from 'lodash'
import { useStateInUrl } from 'features/common/hooks/useStateInUrl'
import { useMeUser } from 'features/users/hooks/useMeUser'
import { isGuest } from 'features/users/model/user'

const DEFAULT_ORDER = {
  by: MapFieldToOrderBy.UpdatedAt,
  direction: SortDirection.Desc
}
function MapsFilter() {
  const dispatch = useAppDispatch()
  const filter = useAppSelector((state) => state.maps.myMapsFilters.filter)
  const currentSearch = useAppSelector((state) => state.maps.myMapsFilters.search)
  const currentOwnerFilter = useAppSelector((state) => state.maps.myMapsFilters.filter?.permissions)
  const currentTagsFilter = useAppSelector((state) => state.maps.myMapsFilters.filter?.tags)
  const currentOrder = useAppSelector((state) => state.maps.myMapsFilters.order)

  const [order, setOrder] = useStateInUrl('order', currentOrder || DEFAULT_ORDER, Object)
  const [ownerFilter, setOwnerFilter] = useStateInUrl('permissions', currentOwnerFilter)
  const [tagsFilter, setTagsFilter] = useStateInUrl('tags', currentTagsFilter, Array)
  const [search, _setSearch] = useStateInUrl('search', currentSearch)

  const user = useMeUser()
  const userIsGuest = isGuest(user)

  const setSearch = (value) => {
    if (value === search) {
      return
    }
    _setSearch(value)
  }

  const mapsSearchBarRef = useRef<MapsSearchBarRefProps>(null)
  const clear = useCallback(() => {
    if (!mapsSearchBarRef.current) return
    mapsSearchBarRef.current.clear()
  }, [mapsSearchBarRef])

  useEffect(() => {
    if (search) {
      mapsSearchBarRef.current?.setValue(search)
    }
  }, [mapsSearchBarRef, search])

  useEffect(() => {
    let needReloadMaps = false
    if (search !== currentSearch) {
      dispatch(setMapFilterSearch(encodeURIComponent(search)))
      needReloadMaps = true
    }
    if (!isEqual(order, currentOrder)) {
      dispatch(setMapFilterOrder(order))
      needReloadMaps = true
    }
    if (!isEqual(currentOwnerFilter, ownerFilter) || !isEqual(currentTagsFilter, tagsFilter)) {
      dispatch(setMapFilter({ permissions: ownerFilter, tags: tagsFilter }))
      needReloadMaps = true
    }

    if (needReloadMaps) {
      dispatch(setMyMapsStatus(RequestStatus.Idle))
      dispatch(setMapFilterPagination({ page: 1 }))
    }
  }, [
    dispatch,
    currentSearch,
    search,
    currentOrder,
    order,
    currentOwnerFilter,
    ownerFilter,
    currentTagsFilter,
    tagsFilter
  ])

  const handleFilterChange = (type: 'permissions' | 'tags', filter) => {
    if (type === 'permissions') {
      handleOwnerFilterChange(filter as MapOwnerFilter)
    } else {
      handleTagsFilterChange(filter as string[])
    }
  }

  const handleOwnerFilterChange = (filter: MapOwnerFilter) => {
    setOwnerFilter(filter)
  }

  const handleTagsFilterChange = (tags: string[]) => {
    setTagsFilter(tags)
  }

  return (
    <BasicPageFilterBar clearResults={<MapsClearSearch onClear={clear} />}>
      <MapsSearchBar ref={mapsSearchBarRef} onSearchChange={setSearch} />
      {!userIsGuest && <MapsFilterSelector filter={filter} onFilterChange={handleFilterChange} />}
      <MapsOrderSelector order={order} onOrderChange={setOrder} />
    </BasicPageFilterBar>
  )
}

export default MapsFilter
